import { ChangeDetectorRef, Component, isDevMode, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AccountTabs, PasswordScoreEnum } from 'account-management';
import { environment } from '../environments/environment';
import { IMenuBarItem, StarbaseMenuBarComponent } from 'starbase-menu-bar';
import { StarbaseNotificationHistoryComponent, StarbaseNotificationSentiment, StarbaseNotificationService, StarbaseNotificationTypes } from 'starbase-notification';
import { IUserTokenModel, StarbaseLoginService } from 'starbase-login';
import { FilterParams, IUserAccountModel } from 'starbase-launcher';
import { LU_JobCategory, LU_JobCode, LU_MaterialService, LU_TermsModel, RateScheduleHeader, ScaleLinkService, Site, STContact, STLog, TimeZone, TimeZones, ScaleTechCoreModule } from 'scaletech-core';
import { NewTableEditorUserViewOptions } from 'new-table-editor';
import { debounceTime, forkJoin, map, Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { MatSliderChange } from '@angular/material/slider';
import { UserDataService } from './services/user-data.service';
import { STContactDataService } from './services/st-contact-data.service';
import { SiteDataService } from './services/site-data.service';
import { StarbaseComponentMessageService } from './services/starbase-component-message.service';
import { LogDataService } from './services/log-data.service';
import { ApiDataService } from './services/api-data.service';
import { AppGlobalsService } from './services/app-globals.service';
import { StarbaseModalOverlayService } from 'modal-overlay';
import { AppInsightsService } from './services/app-insights.service';
import { StarbaseServiceProviderService } from './services/starbase-service-provider.service';
import { JobDataService } from './services/job-data.service';
import { RateScheduleDataService } from './services/rate-schedule-data.service';
import { LU_MaterialServiceService } from './services/lookups/lu-material-service.service';
import { LU_JobCodeService } from './services/lookups/lu-job-code.service';
import { LU_JobCategoryService } from './services/lookups/lu-job-category.service';
import { LU_TermService } from './services/lookups/lu-term.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  public static appMinimumPwdScore = PasswordScoreEnum.Strong;
  public static appMiniumPwdLength = 8;

  @ViewChild('notiHistory', { static: true }) notiHistory: StarbaseNotificationHistoryComponent;
  @ViewChild('optionsModal', {static: true}) optionsModalTemplate: TemplateRef<any>;
  @ViewChild('mainMenu', { static: true}) mainMenu: StarbaseMenuBarComponent;
  @ViewChild('recordHistoryModal', { static: true }) recordHistoryModalTemplate: TemplateRef<any>;
  @ViewChild('helpAboutModal', { static: true }) helpAboutModalTemplate: TemplateRef<any>;

  public title: string;
  public version = environment.VERSION;
  public currentYear: number;

  tooltipDelay = 500;
  darkMode = false;
  glassMode = false;

  menuBarColor = '#911';
  colorPalette = [
    '#af3f0b', '#911', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a'
  ];
  timezone: string
  timeZone: TimeZone;
  timezones = TimeZones;
  isDSTObserved: boolean;

  adminMenuBarItems: IMenuBarItem[]; // save for later customizations
  defaultMenuBarItems: IMenuBarItem[];

  private siteMenuBarItem: IMenuBarItem = {
    displayText: 'Site',
    enabled: true,
    iconClass: 'fa-map',
    clickAction: () => {
      this.openSiteEditor();
    }
  };
  private sitesMenuBarItem: IMenuBarItem = {
    displayText: 'Sites',
    enabled: true,
    iconClass: 'fa-map',
    clickAction: () => {
      this.openSiteEditor();
    }
  };
  private aboutMenuBarItem: IMenuBarItem = {
    displayText: 'About',
    enabled: true,
    iconClass: 'fa-info-circle',
    clickAction: () => {
      this.helpButtonPressed();
    }
  };

  displayName = '';
  loggedInUser: IUserTokenModel = null;
  public currentUserAccount: IUserAccountModel = null;
  overlayEnabled = false;
  public currentAccountTab = AccountTabs.Account;
  recordHistoryCols = STLog.LogColumns(false, false, false);
  recordHistoryData: FilterParams<STLog>;
  recordHistoryLoading = false;
  viewOptsID = 'recordHistory';
  recordHistoryViewOpts = new NewTableEditorUserViewOptions(true);
  openOnUser = false;
  viewSiteEditor = false;
  public rateHeader: RateScheduleHeader = null;
  public copyRateWizOpen = false;
  public viewRateSchedules = false;
  materialsAndServices: LU_MaterialService[] = [];
  sites: Site[] = [];
  customers: STContact[] = [];
  jobCodes: LU_JobCode[] = [];
  jobCategories: LU_JobCategory[] = [];
  terms: LU_TermsModel[];
  displayTooltip: boolean = this.appGlobals.getTooltipDisplay() != null ? this.appGlobals.getTooltipDisplay() : true;

  appMinimumPwdScore = AppComponent.appMinimumPwdScore;
  appMinimumPwdLength = AppComponent.appMiniumPwdLength;

  recordHistoryViewOptionsSubscription: Subscription;

  constructor(
    public userSvc: UserDataService,
    public contactSvc: STContactDataService,
    public jobSvc: JobDataService,
    public rateSvc: RateScheduleDataService,
    public siteSvc: SiteDataService,
    public matServSvc: LU_MaterialServiceService,
    public jobCodeSvc: LU_JobCodeService,
    public jobCatSvc: LU_JobCategoryService,
    public termSvc: LU_TermService,
    public sbSvcProvider: StarbaseServiceProviderService,
    public sbCmpMsgSvc: StarbaseComponentMessageService,
    public logSvc: LogDataService,
    private apiDataSvc: ApiDataService,
    public appGlobals: AppGlobalsService,
    private loginSvc: StarbaseLoginService,
    private notiSvc: StarbaseNotificationService,
    private modalSvc: StarbaseModalDialogService,
    private overlaySvc: StarbaseModalOverlayService,
    private appInsights: AppInsightsService,
    private router: Router,
    private cd: ChangeDetectorRef,
    public scaleLinkSvc: ScaleLinkService) {
      this.title = this.appGlobals.appTitle;
      this.currentYear = new Date().getFullYear();
      this.darkMode = this.appGlobals.getStoredDarkModeSetting() || false;
      this.glassMode = this.appGlobals.getStoredGlassModeSetting() || false;
    }

  ngOnInit() {
    this.version = this.appGlobals.appVersion;
    this.appInsights.logPageView('App loaded', '/');
    // eslint-disable-next-line no-console
    console.info(`Loading ng-webapp ${this.title} ${this.version}`);
    if (isDevMode()) {
      console.warn('The application is running in dev mode.');
    }
    this.adminMenuBarItems = [
      ...this.mainMenu.defaultMenuBarItems.slice(0, 2),
      this.appGlobals.oneSiteMode ? this.siteMenuBarItem : this.siteMenuBarItem,
      ...this.mainMenu.defaultMenuBarItems.slice(2, 3),
      this.aboutMenuBarItem,
      ...this.mainMenu.defaultMenuBarItems.slice(5)
    ];

    // parse version info for tooltip
    this.apiDataSvc.getData(undefined, undefined, 1).subscribe((d: any) => { // TODO: define type for d as apiInfo from server.
      this.version
        += '\nAPI version: '
        + d.apiVersion
        + '\nStarbase Core version: '
        + d.starbaseVersion;
      // eslint-disable-next-line no-console
      console.info('Connected to API server '
        + d.apiVersion
        + '; running Starbase version '
        + d.starbaseVersion);
      if (d.apiVersion.includes('test') || d.apiVersion.includes('dev')) {
        const testingModal = new StarbaseModalOptions(
          `${this.title} Testing Mode`,
          `You are connected to the testing or dev system. ${this.version}`,
          [],
          StarbaseModalTypes.Notice);
        testingModal.forcedWidth = '500px';
        this.modalSvc.postModal(testingModal);
      }
    });
    const color = this.appGlobals.getMenuColor();
    this.menuBarColor = color ? color : '#911'; // testing red color

    this.defaultMenuBarItems = [
      ...this.mainMenu.defaultMenuBarItems.slice(0,4),
      this.aboutMenuBarItem,
      ...this.mainMenu.defaultMenuBarItems.slice(5)
    ];
    this.adminMenuBarItems = [
      ...this.defaultMenuBarItems.slice(0, 2),
      this.appGlobals.oneSiteMode ? this.siteMenuBarItem : this.siteMenuBarItem,
      ...this.defaultMenuBarItems.slice(2)
    ];
    this.mainMenu.menuBarItems = this.defaultMenuBarItems;

    if (this.loginSvc.isLoggedIn()) { // if user is already logged in through cache...
      this.getUserInfoFromSvc();
      this.siteSvc.getPrimarySite().subscribe((site) => {
        this.appGlobals.site = site;
        this.defaultMenuBarItems = [
          ...this.mainMenu.defaultMenuBarItems.slice(0,3),
          this.aboutMenuBarItem,
          ...this.mainMenu.defaultMenuBarItems.slice(5)
        ];
        this.adminMenuBarItems = [
          ...this.defaultMenuBarItems.slice(0, 2),
          this.appGlobals.oneSiteMode ? this.siteMenuBarItem : this.siteMenuBarItem,
          ...this.defaultMenuBarItems.slice(2)
        ];
        if (this.isAdmin() || this.loggedInUser.hasPermissions('invoicewrite')) {
          this.mainMenu.menuBarItems = this.adminMenuBarItems;
        } else {
          this.mainMenu.menuBarItems = this.defaultMenuBarItems;
        }
        if (this.appGlobals.site && this.appGlobals.currentUser) {
          this.timeZone = this.appGlobals.timezone;
          this.timezone = this.timeZone.standardTimeCode;
          this.isDSTObserved = this.appGlobals.getDSTObserved();
          if (this.isDSTObserved === undefined || this.isDSTObserved === null) {
            this.isDSTObserved = this.appGlobals.site.isDSTObserved;
          }
        }
      });
    }
    this.loginSvc.loginState.subscribe((s: boolean) => {
      if (s === true) {
        this.getUserInfoFromSvc();
        this.scaleLinkSvc.registerScales();
      } else {
        this.displayName = '';
        this.loggedInUser = null;
        //this.logOutNotification('Your session has expired. Please log in again.');
        this.router.navigateByUrl('login/out');
      }
    });
    // wire up overlay service
    this.overlaySvc.overlayEmitter.subscribe((o: boolean) => this.overlayEnabled = o);
    this.modalSvc.modalEmitter.subscribe((m: any) => {
      if (m !== null) {
        this.overlaySvc.enableOverlay();
      } else {
        this.overlaySvc.disableOverlay();
      }
    });

    const viewOptions = this.appGlobals.getViewOptions(this.viewOptsID);
    if (viewOptions) {
      viewOptions.optionsChanged = new Subject();
      this.recordHistoryViewOpts = viewOptions;
    }

    this.recordHistoryViewOptionsSubscription =  this.recordHistoryViewOpts.optionsChanged.subscribe((opts) => {
      const temp = opts.optionsChanged;
      opts.optionsChanged = undefined;
      this.appGlobals.setViewOptions(opts, this.viewOptsID);
      opts.optionsChanged = temp;
    });

  }

  ngOnDestroy() {
    if (this.recordHistoryViewOptionsSubscription) {
      this.recordHistoryViewOptionsSubscription.unsubscribe();
    }
  }

  logOut(): void {
    this.loginSvc.logOut();
    this.logOutNotification();
    this.overlaySvc.disableOverlay();
    this.appGlobals.clearSite();
    this.appGlobals.currentUser = '';
    this.terms = [];
  }
  private logOutNotification(msg?: string) {
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder(
        'Logged out',
        msg || 'You have logged out of Starbase.',
        StarbaseNotificationTypes.Warning,
        3000
      )
    );
  }

  public isAdmin(): boolean {
    return (this.loggedInUser) ? this.loggedInUser.isAdmin() : false;
  }

  public optionsPressed(): void {
    console.log('User requested app options.');
    this.displayTooltip = this.appGlobals.getTooltipDisplay() != null ? this.appGlobals.getTooltipDisplay() : true;
    this.modalSvc.postModal(
      new StarbaseModalOptions(
        'ScaleTech Options',
        '',
        [],
        StarbaseModalTypes.Notice,
        this.optionsModalTemplate
      )
    );
  }

  public helpButtonPressed(): void {

    this.modalSvc.postModal(
      new StarbaseModalOptions(
        'About',
        '',
        [],
        StarbaseModalTypes.Info,
        this.helpAboutModalTemplate
      )
    );
  }

  openDashboardSettings() {
    this.modalSvc.closeModal('Options modal was forced to close by application.');
    this.router.navigateByUrl('home');
    this.appGlobals.callDashboardCustomizationsModal.next(true);
  }

  public toggleNotificationHistory(): void {
    this.notiHistory.showSessionNotifications();
  }

  private getUserInfoFromSvc() {
    this.loggedInUser = this.loginSvc.getUserModel();
    this.displayName = this.loggedInUser.displayName;
    if (this.isAdmin()) {
      this.mainMenu.menuBarItems = this.adminMenuBarItems;
    } else {
      this.mainMenu.menuBarItems = this.mainMenu.defaultMenuBarItems;
    }
    this.appGlobals.currentLoggedInUser = this.userSvc.getData(this.userSvc.GetMyAccountUri, '');
    this.appGlobals.currentLoggedInContact = this.appGlobals.currentLoggedInUser
    .pipe(map(
      (user) => {
        this.appGlobals.currentUser = user.user;
        this.darkMode = this.appGlobals.getStoredDarkModeSetting();
        const color = this.appGlobals.getMenuColor();
        this.menuBarColor = color ? color : '#911'; // testing red color
        if (this.appGlobals.site && this.appGlobals.currentUser) {
          this.timeZone = this.appGlobals.timezone;
          this.timezone = this.timeZone.standardTimeCode;
          this.isDSTObserved = this.appGlobals.getDSTObserved();
          if (this.isDSTObserved === undefined || this.isDSTObserved === null) {
            this.isDSTObserved = this.appGlobals.site.isDSTObserved;
          }
        }
        return user.contact
      }
    ));
    this.cd.detectChanges();
  }

  /** Method to update main stylings for app scheme 'normal', 'dark', or 'glass' */
  setStyle(scheme: string): void {
    if (scheme === 'normal') {
      this.darkMode = false;
      this.glassMode = false;
    } else if (scheme === 'dark') {
      this.darkMode = true;
      this.glassMode = false;
    } else if (scheme === 'glass') {
      this.darkMode = false;
      this.glassMode = true;
    }
    this.appGlobals.setDarkMode(this.darkMode);
    this.appGlobals.setGlassMode(this.glassMode);
    this.cd.detectChanges();
  }

  setMenuColor(rgba: any): void {
    this.menuBarColor = rgba.color.hex;
    this.appGlobals.setMenuColor(rgba.color.hex);
  }

  setTimeZone(timezone: string) {
    this.timezone = timezone;
    this.timeZone = TimeZones.find(t => t.standardTimeCode === timezone);
    this.appGlobals.setTimeZone(this.timeZone);
    this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Time Settings Changed', 'In order to apply time changes refresh your browser.'));
  }

  setDSTObserved(dstObserved: boolean) {
    this.isDSTObserved = dstObserved;
    this.appGlobals.setDSTObserved(dstObserved);
    this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Time Settings Changed', 'In order to apply time changes refresh your browser.'));
  }

  /** Method to set tooltip delay for site. */
  setTooltipDelay(delay: MatSliderChange): void {
    this.tooltipDelay = delay.value * 1000;
    // TODO: update app Globals
  }

  /** Method to set tooltip delay for site. */
  setTooltipDisplay(bool: boolean): void {
    this.modalSvc.closeModal('App forced Options modal to close.');
    const yesBtn = new StarbaseModalButton('Ok',
      () => {
        console.info('User changed tooltip display.');
        this.displayTooltip = bool
        this.appGlobals.setTooltipDisplay(bool);
        window.location.reload();
        this.modalSvc.closeModal('User chose to apply changes.');
      });
    const noBtn = new StarbaseModalButton('Cancel',
      () => {
        this.modalSvc.closeModal('User chose to cancel.');
      });
    this.modalSvc.postModal(new StarbaseModalOptions(
      'Apply Changes',
      `Applying this change will require the site to reload to apply`,
      [yesBtn, noBtn],
      StarbaseModalTypes.Warning
    ));
  }

  /** Method to reset site to default preferences. */
  clearPrefs(): void {
    this.modalSvc.closeModal('App forced Options modal to close.');
    const yesBtn = new StarbaseModalButton('Reset',
      () => {
        if (!this.appGlobals.clearAll()) {
          this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
            'Error', 'Could not clear preferences.',
            StarbaseNotificationTypes.Error
          ));
        } else {
          // eslint-disable-next-line no-console
          console.info('User preferences cleared from browser.');
          window.location.reload();
          //this.router.navigateByUrl('/'); // return to login screen to reload app
        }
        this.modalSvc.closeModal('User chose to reset preferences.');
      });
    const noBtn = new StarbaseModalButton('Cancel',
      () => {
        this.modalSvc.closeModal('User chose to cancel.');
      });
    this.modalSvc.postModal(new StarbaseModalOptions(
      'Reset Your Preferences?',
      `Are you sure you want to reset all ScaleTech preferences? This only affects
      options you've chosen on this browser. If you are logged in anywhere else, those
      options will remain.`,
      [yesBtn, noBtn],
      StarbaseModalTypes.Warning
    ));
  }

  handleRecordHistoryRequest(guid: string, forUser = false, enableOverlay = true) {
    if (!guid || guid === '') {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Unknown entity',
          'Sorry, record history is not available for that entity.',
          StarbaseNotificationTypes.Notice
        )
      );
      return;
    }
    this.recordHistoryLoading = true;
    if (forUser) {
      this.modalSvc.closeModal('Requested History');
    }
    this.modalSvc.postModal(new StarbaseModalOptions(
      'Record History',
      '',
      [
        new StarbaseModalButton(
          'Back',
          forUser ?
          () => {
            this.modalSvc.closeModal('User Clicked Back');
            this.openOnUser = true;
            this.optionsPressed();
            this.openOnUser = false;
          } :
          () => {
            this.modalSvc.closeModal('User Clicked Back');
            if (enableOverlay) {
              this.overlaySvc.enableOverlay(); // this path is for single record pane and thus overlay should remain.
            }
          }
        )
      ],
      StarbaseModalTypes.Info,
      this.recordHistoryModalTemplate
    ));
    const fp = new FilterParams<STLog>();
    fp.rpp = 10;
    fp.addToFilter('EntityGuid', guid);
    this.logSvc.getDataByFilterParamsBroadSearch(this.logSvc.broadSearchUri, fp).subscribe(d => {
      this.logSvc.setLastRetrievedEntityName((d.collection.length) ? d.collection[0].entityName : '');
      this.recordHistoryData = d;
      this.recordHistoryLoading = false;
    },
    e => {
      console.error(e);
      this.recordHistoryLoading = false;
      this.modalSvc.closeModal('Record History failed to load.');
      this.notiSvc.postNotification(this.notiSvc.notificationBuilder('Error', 'Failed to load Record History', StarbaseNotificationTypes.Error, 15000, StarbaseNotificationSentiment.Error));
      if (forUser) {
        this.openOnUser = true;
        this.optionsPressed();
        this.openOnUser = false;
      }
      if (enableOverlay) {
        this.overlaySvc.enableOverlay(); // this path is for single record pane and thus overlay should remain.
      }
    }
  );
  }

  score = (pwd: string) => this.userSvc.getRequisiteData(this.userSvc.PasswordScoreUri + pwd).pipe(
    debounceTime(1000),
    map(s => s as PasswordScoreEnum)
  );

  openSiteEditor() {
    this.viewSiteEditor = true;
    this.termSvc.getAllData(this.termSvc.getAllUri).subscribe((term) =>{
      this.terms = term
    });
    this.overlaySvc.enableOverlay();
  }

  closeSiteEditor() {
    this.viewSiteEditor = false;
    this.terms = [];
    this.overlaySvc.disableOverlay();
  }

  openRSModal(site: Site) {
    this.matServSvc.getAllData(this.matServSvc.getAllUri).subscribe(
      (matServs) => {
        this.materialsAndServices = matServs;
        this.rateHeader = site.rateScheduleHeader;
        if (!this.rateHeader) {
          this.rateHeader = new RateScheduleHeader();
          this.rateHeader.uid = 0;
          this.rateHeader.site = site;
          this.rateHeader.siteUID = site.uid;
        }
        this.viewRateSchedules = true;
        this.overlaySvc.disableOverlay();
        this.overlaySvc.enableOverlay();
      }
    )

  }

  closeRateSchedules() {
    this.viewRateSchedules = false;
    this.overlaySvc.disableOverlay();
    this.overlaySvc.enableOverlay();
  }

  launchCopyRateScheduleWizard(rate: RateScheduleHeader) {
    forkJoin(
      this.siteSvc.getAllData(this.siteSvc.getAllUri),
      this.contactSvc.getCustomers(),
      this.jobCodeSvc.getAllData(this.jobCodeSvc.getAllUri),
      this.jobCatSvc.getAllData(this.jobCatSvc.getAllUri)
    ).pipe().subscribe(([
        sites,
        customers,
        jobCodes,
        jobCategories
      ]: [
        Site[],
        STContact[],
        LU_JobCode[],
        LU_JobCategory[]
      ]) => {
        this.sites = sites;
        this.customers = customers;
        this.jobCodes = jobCodes;
        this.jobCategories = jobCategories;
      },
      e => {
        console.error(e);
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder('Error Loading',
            'Supporting data for dash modules failed to load.',
            StarbaseNotificationTypes.Error
          )
        );
      },
      () => {
        this.rateHeader = rate;
        this.copyRateWizOpen = true;
      }
    );
  }

  handleCopyRateScheduleWizard(obj) {
    this.copyRateWizOpen = false;

    this.jobSvc.triggerRefresh();
    this.contactSvc.triggerRefresh();
    if (obj) {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Success',
          'Rate Schedule Successfully Copied',
          StarbaseNotificationTypes.Success
        )
      );
    } else {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Error',
          'Failed to Copy Rate Schedule',
          StarbaseNotificationTypes.Error
        )
      );
    }
  }

  cancelCopyRateScheduleWizard(obj) {
    this.copyRateWizOpen = false;
  }
  redirectToAttributions(): void {
    this.modalSvc.closeModal("User clicked to attribution");
    this.router.navigate(['/attribution']);
  }
}
